import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';
import moment from 'moment';
import { buildUrl } from 'utils';
import { Card } from 'components/UI';
import { forceTimezone } from 'utils';
import Loading from 'components/Loading';
import Paginator from 'components/Paginator';
import { useHistory } from 'react-router-dom';
import { DocumentsActions } from 'state/modules/documents';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VersionStatus from 'components/Documents/VersionStatus';
import DocumentModal from 'components/Documents/DocumentModal';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, Typography, Icon } from '@mui/material';
import { transformAsyncErrors } from 'utils/utils';
import { styled } from '@mui/material/styles';

const Container = styled(Box)({
  height: '100%',
  width: '100%',
});

const DocumentItem = styled(Box)(({ theme }) => ({
  border: '1px solid #EEEEEE',
  borderRadius: 4,
  cursor: 'pointer',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2, 1.75),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  transition: 'box-shadow 0.2s',
  '&:hover': {
    boxShadow: theme.shadows[2],
  },
}));

const LoadingContainer = styled(Box)({
  height: 150,
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const StyledButton = styled(Button)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(13),
  textTransform: 'capitalize',
}));

const IconBox = styled(Box)(({ theme, status }) => {
  let bg = theme.palette.documents?.defaultBg || '#F5F5F5';
  let color = theme.palette.documents?.default || '#757575';
  if (status === 'APPROVED') {
    bg = theme.palette.documents?.approvedBg;
    color = theme.palette.documents?.approved;
  } else if (status === 'PENDING') {
    bg = theme.palette.documents?.pendingBg;
    color = theme.palette.documents?.pending;
  } else if (status === 'UPLOADED') {
    bg = theme.palette.documents?.uploadedBg;
    color = theme.palette.documents?.uploaded;
  } else if (status === 'DECLINED') {
    bg = theme.palette.documents?.declinedBg;
    color = theme.palette.documents?.declined;
  } else if (status === 'EXPIRED') {
    bg = theme.palette.documents?.expiredBg;
    color = theme.palette.documents?.expired;
  }
  return {
    alignItems: 'center',
    backgroundColor: bg,
    borderRadius: 2,
    color,
    display: 'flex',
    justifyContent: 'center',
    height: 20,
    width: 20,
    '& svg': {
      fontSize: theme.typography.pxToRem(13),
    },
  };
});

const ArrowIcon = styled(ChevronRightIcon)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.pxToRem(14),
  marginLeft: theme.spacing(1),
}));

const DocumentsWidget = ({ id, type }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { localeFormat } = useSelector((state) => state.organizations);
  const [openCreateDocumentModal, setCreateDocumentModal] = useState(false);
  const [params, setParams] = useState({
    page: 1,
    limit: 3,
    sort_desc: false,
    sort_field: 'id',
  });
  const [asyncErrors, setAsyncErrors] = useState({});

  const documents = useSelector((state) =>
    state.documents.all.map((id) => state.documents.byId[id])
  );
  const { pagination, loading } = useSelector((state) => state.documents);

  // Fetch documents
  const getDocuments = (params) => {
    const payload = { ...params };
    if (id) payload.source_id = id;
    if (type) payload.source_type = type;
    dispatch(DocumentsActions.fetchDocuments(payload));
  };

  // Modal toggle
  const handleOpenCreateDocumentModal = () => setCreateDocumentModal((prev) => !prev);

  // Create document
  const handleCreateDocument = async (document_type_id, description) => {
    const payload = {
      document_type_id,
      description,
      source_id: id,
      source_type: type,
    };
    Object.keys(payload).forEach((key) => !payload[key] && delete payload[key]);
    const response = await dispatch(DocumentsActions.createDocument(payload));
    if (response.ok) {
      handleOpenCreateDocumentModal();
      const newParams = { ...params, page: 1 };
      setParams(newParams);
      getDocuments(newParams);
    } else if (response.problem === 'CLIENT_ERROR') {
      setAsyncErrors((prev) => ({
        ...prev,
        createDocument: transformAsyncErrors(response?.data?.details),
      }));
    }
  };

  // Pagination
  const handleChangePage = (event, newPage) => {
    const newParams = { ...params, page: newPage + 1 };
    setParams(newParams);
    getDocuments(newParams);
  };
  const handleChangeRowsPerPage = (event) => {
    const newParams = { ...params, limit: event.target.value };
    setParams(newParams);
    getDocuments(newParams);
  };

  // Menu items
  const items = [
    {
      title: i18n.t('documents.create_document'),
      can: { action: 'create', name: 'document' },
      onClick: handleOpenCreateDocumentModal,
    },
  ];

  // Title
  const title = (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography color="textPrimary" component="span">
        <Box fontSize={14} fontWeight="fontWeightMedium" data-cy="document-title" component="span">
          {i18n.t('documents.title')}
        </Box>
      </Typography>
      <StyledButton
        type="button"
        variant="text"
        color="primary"
        disableFocusRipple={true}
        data-cy="view-all-documents"
        onClick={() => {
          history.push(buildUrl(`/documents?source_id=${id}&source_type=${type}`));
        }}
      >
        {i18n.t('common.view_all')}
      </StyledButton>
    </Box>
  );

  useEffect(() => {
    getDocuments(params);
    // eslint-disable-next-line
  }, []);

  return (
    <Card title={title} menuItems={items} data-cy="documents-card" headerActionType="vertical">
      <Container data-cy="documents-list">
        {loading ? (
          <LoadingContainer>
            <Loading />
          </LoadingContainer>
        ) : (
          <>
            {documents?.length > 0 ? (
              <>
                {documents.map((document, i) => (
                  <DocumentItem
                    key={i}
                    data-cy={document?.name}
                    status={document?.default?.status}
                    onClick={() => history.push(buildUrl(`/documents/${document?.id}`))}
                  >
                    <Box display="flex" width="60%">
                      <IconBox status={document?.default?.status}>
                        <Icon component={AttachFileIcon} />
                      </IconBox>
                      <Box ml={1} maxWidth="100%">
                        <Typography component="span">
                          <Box sx={{ fontSize: 13, fontWeight: 'bold', whiteSpace: 'normal' }}>
                            {document?.name || document?.document_type?.name}
                          </Box>
                        </Typography>
                        {document?.default?.expiration_time && (
                          <Typography color="textSecondary" component="span">
                            <Box fontSize={12}>
                              Expiration date:{' '}
                              {forceTimezone(moment(document?.default?.expiration_time)).format(
                                `${localeFormat} h:mm A`
                              )}
                            </Box>
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center">
                      {document?.default?.status && (
                        <VersionStatus status={document?.default?.status} />
                      )}
                      <ArrowIcon />
                    </Box>
                  </DocumentItem>
                ))}
                <Paginator
                  component="div"
                  limit={params.limit}
                  pagination={pagination}
                  rowsPerPageOptions={[3]}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            ) : (
              <Typography color="textPrimary">
                <Box fontSize={14} component="span">
                  {i18n.t(`documents.documents_placeholder`)}
                </Box>
              </Typography>
            )}
          </>
        )}
      </Container>
      {openCreateDocumentModal && (
        <DocumentModal
          mode="create"
          open={openCreateDocumentModal}
          handleCreate={handleCreateDocument}
          handleClose={handleOpenCreateDocumentModal}
          asyncErrors={asyncErrors.createDocument}
        />
      )}
    </Card>
  );
};

DocumentsWidget.propTypes = {
  id: PropTypes.number,
  type: PropTypes.string,
};

export default DocumentsWidget;
